import React from 'react';
import { Cross, InfoCircle, Mail } from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import Modal from '../../../../../../../../../../shared/design-system/components/atoms/modal';
import Table from '../../../../../../../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../../../../../../../shared/design-system/components/organisms/table/types';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../../../shared/design-system/components/overlay';
import Select from '../../../../../../../../../../shared/design-system/components/select';
import {
  SpamAssassinScore,
  SpamEmailAccountsDropdown,
} from '../../../../../../../../types';
import { accessibleOnClick } from '../../../../../../../../../../shared/utils/accessible-on-click';

const SpamAssassinReportModal: React.FC<{
  show: boolean;
  onClose: () => void;
  data: SpamAssassinScore;
  emailAccounts?: Array<{ id: number; fromEmail: string; isDeleted: boolean }>;
  selectedEmailAccountId?: number;
  onEmailAccountChange?: (emailAccountId: number) => void;
  spammyEmailAccounts?: Array<{
    fromEmail: string;
    seedTestSenderEmailAccountId: number;
    isDeleted: boolean;
  }>;
  keySummaryModal?: boolean;
  isLoading?: boolean;
  isEmailLoading?: boolean;
  isFromEmail?: string;
}> = ({
  show,
  onClose,
  data,
  // emailAccounts = [],
  selectedEmailAccountId,
  onEmailAccountChange,
  spammyEmailAccounts = [],
  keySummaryModal,
  isLoading,
  isEmailLoading,
  isFromEmail,
}) => {
  const handleClose = () => {
    onClose();
  };

  const getSpamAssassinScoreTag = (score: number) => {
    if (score) {
      if (score < 0) {
        return { label: 'Extremely Safe', key: 'extremely-safe' };
      }
      if (score >= 0 && score <= 2) {
        return { label: 'Safe', key: 'safe' };
      }
      if (score > 2 && score <= 5) {
        return { label: 'Warning', key: 'warning' };
      }
      if (score > 5) {
        return { label: 'Spam Risk', key: 'spam-risk' };
      }
    }
    return { label: '', key: '' };
  };

  const emailAccountOptions = spammyEmailAccounts?.map((account) => ({
    key: account?.seedTestSenderEmailAccountId?.toString(),
    seedTestSenderEmailAccountId: account?.seedTestSenderEmailAccountId,
    fromEmail: account?.fromEmail,
    isDeleted: account?.isDeleted,
  }));

  // Function to get email account ID directly from key
  const getEmailAccountIdFromKey = (key: string): number | null => {
    if (!key) return null;
    const emailAccountId = parseInt(key, 10);
    return isNaN(emailAccountId) ? null : emailAccountId;
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      onHide={handleClose}
      titleContent={
        <div className="spam-assassin-report-modal-header">
          <div className="d-flex flex-column gap-8">
            <div className="d-flex align-items-center justify-content-between">
              <strong>SpamAssassin Test Description</strong>
              <div {...accessibleOnClick(handleClose)}>
                <Cross width={20} height={20} className="blue-txt-15" />
              </div>
            </div>
            <p>
              SpamAssassin flags potential spam risks in your email. Your total
              score is based on the tests below. Fixing the high-score items
              will improve your chances of landing in the inbox.{' '}
              <a
                href="https://spamassassin.apache.org/old/tests_3_3_x.html"
                target="_blank"
                rel="noreferrer"
                className="blue-txt-31"
              >
                Learn more
              </a>
            </p>
          </div>
        </div>
      }
      backdrop="static"
      showCloseIcon={false}
      className="spam-assassin-report-modal-container"
      hideCancelButton={true}
      hideSubmitButton={true}
      hideFooter={true}
      extraModalProps={{
        enforceFocus: false,
      }}
    >
      <div className="d-flex align-items-center justify-content-between w-100 pb-3 pt-3">
        {keySummaryModal ? (
          <div className="spam-assassin-email-account-display">
            <div className="spam-assassin-email-account-display-container">
              {isLoading ? (
                <SkeletonLoading width={260} height={32} />
              ) : (
                <>
                  <Mail width={16} height={16} className="gray-txt-15" />
                  <span className="email-account-name">{isFromEmail}</span>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="spam-assassin-email-account-select">
            {isEmailLoading ? (
              <SkeletonLoading width={260} height={32} />
            ) : (
              <Select<SpamEmailAccountsDropdown>
                options={emailAccountOptions}
                selectedOptionKey={selectedEmailAccountId?.toString()}
                optionRenderer={(option) => (
                  <OverlayTooltip
                    placement={Placement.Right}
                    text={`${option?.fromEmail} ${
                      option?.isDeleted ? '(Deleted)' : ''
                    }`}
                  >
                    <span>
                      {option?.fromEmail}
                      {option?.isDeleted && (
                        <span className="email-account-item-deleted">
                          (Deleted)
                        </span>
                      )}
                    </span>
                  </OverlayTooltip>
                )}
                showOptionsSeparator={true}
                selectedOptionRenderer={([option]) => (
                  <div className="d-flex align-items-center gap-6">
                    <Mail width={16} height={16} className="gray-txt-15" />
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text={option?.fromEmail}
                    >
                      <span>{option?.fromEmail}</span>
                    </OverlayTooltip>
                  </div>
                )}
                onChange={([option]) => {
                  if (option && onEmailAccountChange) {
                    // Get the email account ID directly from the key
                    const emailAccountId = getEmailAccountIdFromKey(option.key);

                    if (emailAccountId) {
                      onEmailAccountChange(emailAccountId);
                    }
                  }
                }}
                showSearch
                autoFocusSearch
                searchPlaceholder="Search email accounts"
                optionFilterProp="fromEmail"
                filterOption={(value, option) =>
                  option?.fromEmail?.toLowerCase().includes(value.toLowerCase())
                }
                placeholder={
                  <div className="d-flex align-items-center gap-6">
                    <Mail width={16} height={16} className="gray-txt-15" />
                    <span>Email account</span>
                  </div>
                }
                placement={Placement.Bottom}
                className="spam-assassin-email-select"
              />
            )}
          </div>
        )}
        <div className="total-spam-score">
          {isLoading ? (
            <SkeletonLoading width={95} height={20} />
          ) : (
            <>
              <div>
                <span className="spam-assassin-report-modal-spam-score">
                  Spam Score:{' '}
                </span>
                <span
                  className={`score-cell ${
                    getSpamAssassinScoreTag(data?.score)?.key
                  }`}
                >
                  {data?.score}
                </span>
              </div>

              {data?.score && (
                <span
                  className={`spamassasin-score-tag ${
                    getSpamAssassinScoreTag(data?.score)?.key
                  }`}
                >
                  {getSpamAssassinScoreTag(data?.score)?.label}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="spam-assassin-report-modal-table">
        <Table
          columns={[
            {
              dataField: 'testName',
              text: 'Spam Test Name',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>Spam Test Name</span>
                  <OverlayTooltip
                    text={
                      <span>
                        SpamAssassin performs multiple tests on different parts
                        of your email — including header, body, raw body, URLs,
                        and more. <br />
                        <br />
                        It’s an open-source program, and the test list is
                        continuously updated by the community.
                      </span>
                    }
                    className="spam-assassin-report-modal-tooltip"
                  >
                    <InfoCircle className="gray-txt-11" />
                  </OverlayTooltip>
                </div>
              ),
              component: (cell) => <span className="name-cell">{cell}</span>,
              headerLoadingSkeleton: (
                <SkeletonLoading width={110} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={200} height={20} />,
            },
            {
              dataField: 'testDescription',
              text: 'Spam Test Description',
              component: (cell) => (
                <span className="description-cell">{cell}</span>
              ),
              headerLoadingSkeleton: (
                <SkeletonLoading width={130} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={200} height={20} />,
            },
            {
              dataField: 'score',
              text: 'Score',
              component: (cell) => (
                <span className={`score-cell ${cell > 0 ? 'red' : 'green'}`}>
                  {cell}
                </span>
              ),
              headerLoadingSkeleton: (
                <SkeletonLoading width={110} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={150} height={20} />,
            },
          ]}
          data={data?.spamReport?.tests || []}
          pagination={PaginationShowHide.HIDE}
          paginationOptions={{
            options: {
              limit: data?.spamReport?.tests?.length,
              page: 1,
              totalElements: data?.spamReport?.tests?.length,
            },
          }}
          isLoading={isLoading}
        />
      </div>
    </Modal>
  );
};

export default SpamAssassinReportModal;
